import {
  FacebookFillIcon,
  InstagramFillIcon,
  LinkedInFillIcon,
  MessageFillIcon,
  PhoneFillIcon,
  TwiterFillIcon,
  YoutubeFillIcon,
} from "@/components/Icons";
import useFetch from "@/hooks/useFetch";
import { getBuilderItem } from "@/requests/builder";
import { useAppSelector } from "@/store/hooks";
import { getPageRoute, getS3ImageUrl } from "@/utils/routes";
import Image from "next/image";
import Link from "next/link";
import { CSSProperties, useMemo } from "react";
import classes from "./Footer.module.css";

interface Props {
  className?: string;
  style?: CSSProperties;
}

const Footer = ({ className, style }: Props) => {
  const contactInfo =
    useAppSelector((state) => state.global.socialLinksWidget) || {};

  const currentYear = new Date().getFullYear();

  const footer1 = useFetch(() => getBuilderItem("menus", "footer_menu_1"), {});
  const footer2 = useFetch(() => getBuilderItem("menus", "footer_menu_2"), {});
  const footer3 = useFetch(() => getBuilderItem("menus", "footer_menu_3"), {});


  const footerMenu1: MenuItem[] = useMemo(
    () => footer1.response?.menu || [],
    [footer1]
  );
  const footerMenu2: MenuItem[] = useMemo(
    () => footer2.response?.menu || [],
    [footer2]
  );
  const footerMenu3: MenuItem[] = useMemo(
    () => footer3.response?.menu || [],
    [footer3]
  );

  const socialLinks = [
    ...(contactInfo.facebook
      ? [
          {
            title: "Facebook",
            href: contactInfo.facebook,
            Icon: FacebookFillIcon,
          },
        ]
      : []),
    ...(contactInfo.twitter
      ? [
          {
            title: "Twiter",
            href: contactInfo.twitter,
            Icon: TwiterFillIcon,
          },
        ]
      : []),
    ...(contactInfo.instagram
      ? [
          {
            title: "Instagram",
            href: contactInfo.instagram,
            Icon: InstagramFillIcon,
          },
        ]
      : []),
    ...(contactInfo.youTube
      ? [
          {
            title: "YouTube",
            href: contactInfo.youTube,
            Icon: YoutubeFillIcon,
          },
        ]
      : []),
    ...(contactInfo.linkedin
      ? [
          {
            title: "LinkedIn",
            href: contactInfo.linkedin,
            Icon: LinkedInFillIcon,
          },
        ]
      : []),
  ];

  const classNames = [classes.Footer, className];

  return (
    <>
      <div className={classNames.join(" ")} style={style}>
        <div className={`${classes.container} container`}>
          <div className={classes.top}>
            <div className={classes.left}>
              <p className={classes.title}>Newsletter</p>

              <p className={classes.text}>
                Sign up for get latest news and update
              </p>

              <form className={classes.form}>
                <input
                  className={classes.input}
                  type="text"
                  placeholder="Enter your email"
                />
                <button className={classes.button}>Subscribe</button>
              </form>

              <div className={classes.row}>
                <div className={classes.row}>
                  {/* phone */}
                  <Link
                    className={classes.link}
                    href={`tel:${contactInfo.phone}`}
                  >
                    <PhoneFillIcon />
                    <span className={classes.text}>{contactInfo.phone}</span>
                  </Link>
                </div>

                <div className={classes.row}>
                  {/* email */}
                  <Link
                    className={classes.link}
                    href={`mailto:${contactInfo.email}`}
                  >
                    <MessageFillIcon />
                    <span className={classes.text}>{contactInfo.email}</span>
                  </Link>
                </div>
              </div>

              <div className={classes.socialLinks}>
                {socialLinks.map((social, index) => (
                  <Link
                    key={index}
                    className={classes.socialLink}
                    href={social.href || "#"}
                    title={social.title}
                  >
                    <social.Icon />
                  </Link>
                ))}
              </div>
            </div>

            <div className={classes.right}>
              <FooterLinks title="Company" links={footerMenu1} />

              <FooterLinks title="Help" links={footerMenu2} />

              <div className={classes.third}>
                <FooterLinks title="Policy" links={footerMenu3} />

                {/* <FooterLinks
									title="Store Locator"
									links={[
										{
											name: "See our outlets",
											url: "/store-locator",
											submenu: [],
											type: "link",
										},
									]}
									activeLink={true}
								/> */}
              </div>
            </div>
          </div>

          <div className={classes.bottom}>
            <div className={classes.left}>
              <p className={classes.text}>
                Copyright @ {currentYear} Gadget & Gear. All rights reserved.
              </p>
            </div>

            <div className={classes.right}>
              {contactInfo.methods?.url && (
                <Image
                  src={getS3ImageUrl(contactInfo.methods?.url)}
                  width={400}
                  height={40}
                  unoptimized={true}
                  alt="payment methods"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {/* <div className={classes.FooterBottom}>
				<p className={classes.text}>
					Designed & Developed by{" "}
					<Link
						href="https://bitbytetechnology.com/"
						className={classes.link}>
						Bit Byte Technology Ltd.
					</Link>
				</p>
			</div> */}
    </>
  );
};

const FooterLinks = ({
  title,
  links = [],
  activeLink = false,
}: {
  title: string;
  links?: MenuItem[];
  activeLink?: boolean;
}) => {
  const phoneNumber = useAppSelector(
    (state) => state.global.socialLinksWidget?.phone || ""
  );

  let classNames = `${classes.footerLinks} ${
    activeLink ? classes.activeLink : ""
  }`;

  return (
    <div className={classNames}>
      <div className="flex justify-center md:justify-start">
        {title === "Company" && phoneNumber && (
          <div>
            <Link
              href={`tel:${phoneNumber}`}
              className="group flex gap-1 border border-primary text-primary w-min whitespace-nowrap rounded-lg py-2 px-2 xl:px-4 mb-4 hover:border-white"
            >
              <PhoneFillIcon
                // width={30}
                // height={30}
                className={`${classes.icon} fill-primary group-hover:fill-white duration-500 xl:w-8 xl:h-8`}
              />
              <span className="text-primary text-base xl:text-2xl font-bold group-hover:text-white duration-500">
                {phoneNumber}
              </span>
            </Link>
          </div>
        )}
      </div>

      <h3 className={classes.title}>{title}</h3>

      <ul className={classes.list}>
        {links.map((link, index) => (
          <li
            className={`${classes.item} ${
              link.name?.toLowerCase()?.includes("store") ? classes.store : ""
            }`}
            key={index}
          >
            <Link
              className={classes.link}
              href={
                getPageRoute((link.type || "custom") as any, link.url || "") ||
                ""
              }
            >
              {link.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Footer;
